import React, {useContext, useEffect, useRef, useState} from 'react';
import {Platform, StyleSheet, View, Text, TouchableOpacity} from 'react-native';
import {useSelector} from 'react-redux';
import {I18nContext} from 'i18n/context/I18nContext';

import { TYPE, FONTS } from 'style/typography';

/**
 * Safety net for a loading screen that has silently frozen.
 *
 * A connection that fails outright is already handled by
 * ConnectionFailureOverlay, which flags the failure and offers the same
 * recovery prompt. This covers the case where nothing is flagged but the load
 * simply stops advancing (the bar parks at its cap and never completes).
 *
 * The timer resets on every progress signal (room state / rpm / content /
 * ready-to-move changes), so a slow-but-steadily-progressing load keeps it
 * disarmed. It only fires when the loading screen emits nothing at all for the
 * full window.
 */
const STALL_TIMEOUT_MS = 90000;

export default function StuckLoadingOverlay() {
  const roomState = useSelector((state) => state.communication?.room?.state);
  const worldLoading = useSelector((state) => state.world?.worldLoading);
  const rpmReady = useSelector((state) => state.meeting?.meeting?.rpmReady);
  const contentReady = useSelector(
    (state) => state.meeting?.meeting?.contentReady,
  );
  const readyToMove = useSelector(
    (state) => state.meeting?.meeting?.ready_to_move,
  );

  const {translate} = useContext(I18nContext);

  const [stalled, setStalled] = useState(false);
  const timerRef = useRef(null);

  // Safety-net stall timer. Any change to worldLoading or a progress signal
  // re-runs this effect, which clears + rearms the timer — so continuous
  // progress keeps it from ever firing. It only fires when the loading screen
  // sits with no change at all for the whole window.
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!worldLoading) {
      setStalled(false);
      return undefined;
    }

    timerRef.current = setTimeout(() => setStalled(true), STALL_TIMEOUT_MS);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [worldLoading, roomState, rpmReady, contentReady, readyToMove]);

  if (!stalled) return null;

  const isWeb = Platform.OS === 'web';

  const handleAction = () => {
    if (isWeb) {
      if (typeof window !== 'undefined' && window.location) {
        window.location.reload();
      }
      return;
    }
    try {
      // Lazy require so web bundles never touch the native module.
      const RNRestart = require('react-native-restart').default;
      RNRestart.Restart();
    } catch (_) {
      // If restart is unavailable, the message still instructs the user
      // to close and reopen the app manually.
    }
  };

  return (
    <View style={styles.container} pointerEvents="auto">
      <View style={styles.card}>
        <Text style={styles.title}>{translate('stuckLoading.title')}</Text>
        <Text style={styles.message}>
          {isWeb
            ? translate('stuckLoading.message')
            : translate('stuckLoading.messageNative')}
        </Text>
        <TouchableOpacity
          style={styles.button}
          onPress={handleAction}
          activeOpacity={0.85}>
          <Text style={styles.buttonText}>
            {isWeb
              ? translate('stuckLoading.refresh')
              : translate('stuckLoading.restart')}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 10000,
    elevation: 10000,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.85)',
    padding: 24,
  },
  card: {
    maxWidth: 340,
    width: '100%',
    alignItems: 'center',
    backgroundColor: 'rgba(15,16,20,0.98)',
    borderWidth: 1,
    borderColor: 'rgba(197,232,54,0.30)',
    borderRadius: 18,
    paddingVertical: 28,
    paddingHorizontal: 26,
  },
  title: {
    color: '#F6FBFF',
    fontWeight: '700',
    fontSize: TYPE.lg,
    fontFamily: FONTS.heading,
    textAlign: 'center',
  },
  message: {
    color: 'rgba(246,251,255,0.75)',
    fontWeight: '500',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
    lineHeight: 20,
    textAlign: 'center',
    marginTop: 12,
  },
  button: {
    marginTop: 22,
    backgroundColor: '#C5E836',
    borderRadius: 24,
    paddingVertical: 12,
    paddingHorizontal: 32,
  },
  buttonText: {
    color: '#0F1014',
    fontWeight: '700',
    fontSize: TYPE.base,
    fontFamily: FONTS.body,
  },
});
